import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useAppSelector } from "@store/store-helper";
import { companyMembersByRoleSelector, companyMembersSelector, fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { InfoBar } from "@components/common/info-bar/info-bar";
import { InfoBarCounts } from "@components/common/info-bar/info-bar-counts";
import { useMediaQueryList } from "@hooks/use-media-query";
import { InviteMemberToCompany } from "@pages/members/invite-member-to-company";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";

/** How many workspace members hold each of the roles counted in the info bar */

/** The role counts of the workspace members */
function getRoleCounts(roleCounts, canAssignManagers) {
  // Workspaces that cannot assign managers have a single admin and otherwise only viewers
  if (!canAssignManagers) {
    return [{
      label: "Viewers",
      value: roleCounts.viewers
    }, {
      label: "Group Viewers",
      value: roleCounts.groupViewers
    }];
  }
  return [{
    label: "Admins",
    value: roleCounts.admins
  }, {
    label: "Viewers",
    value: roleCounts.viewers
  }, {
    label: "Group Managers",
    value: roleCounts.groupManagers
  }, {
    label: "Group Viewers",
    value: roleCounts.groupViewers
  }, {
    label: "Project Managers",
    value: roleCounts.projectManagers
  }];
}

/**
 * Shows the main details for members
 */
export function MainMemberDetails() {
  const $ = _c(26);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    isFetchingCompanyMembers
  } = useAppSelector(fetchingMembersFlagsSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = companyMembersByRoleSelector("COMPANY_EXECUTIVE");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const adminMembers = useAppSelector(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = companyMembersByRoleSelector("COMPANY_MANAGER");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const groupManagerMembers = useAppSelector(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = companyMembersByRoleSelector("PROJECT_MANAGER");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const projectManagerMembers = useAppSelector(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = companyMembersByRoleSelector("COMPANY_VIEWER");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const viewerMembers = useAppSelector(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = companyMembersByRoleSelector("GROUP_VIEWER");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const groupViewers = useAppSelector(t4);
  const companyMembers = useAppSelector(companyMembersSelector);
  const {
    canInviteUsersToCompany
  } = useHasUserValidRoleCompanyLevel();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const totalUsersInWorkspace = companyMembers.length;
  if (isFetchingCompanyMembers) {
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <InfoBar isLoading={true} />;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  let t5;
  if ($[6] !== totalUsersInWorkspace) {
    t5 = {
      label: "Total",
      content: totalUsersInWorkspace
    };
    $[6] = totalUsersInWorkspace;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== adminMembers.length || $[9] !== canAssignManagers || $[10] !== groupManagerMembers.length || $[11] !== groupViewers.length || $[12] !== projectManagerMembers.length || $[13] !== viewerMembers.length) {
    t6 = getRoleCounts({
      admins: adminMembers.length,
      viewers: viewerMembers.length,
      groupManagers: groupManagerMembers.length,
      groupViewers: groupViewers.length,
      projectManagers: projectManagerMembers.length
    }, canAssignManagers);
    $[8] = adminMembers.length;
    $[9] = canAssignManagers;
    $[10] = groupManagerMembers.length;
    $[11] = groupViewers.length;
    $[12] = projectManagerMembers.length;
    $[13] = viewerMembers.length;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = {
      label: "Roles",
      isFlexible: true,
      content: <InfoBarCounts counts={t6} />
    };
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t7) {
    t8 = [t5, t7];
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <InviteMemberToCompany />;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = !canInviteUsersToCompany || isScreenSmOrSmaller;
  let t11;
  if ($[21] !== t10) {
    t11 = {
      content: t9,
      isHidden: t10
    };
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <InfoBar items={t8} action={t11} />;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}